import { Component, EventEmitter, OnInit, Output, Input } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { RequestCreationFormService } from "@fiduciaria/RequestCreation/Services/request-creation-form.service";
import { UploadMultipleFilesService } from "@fiduciaria/RequestCreation/Services/upload-multiple-files.service";
import { TrustService } from "@fiduciaria/Shared/Services/trust.service";
import { RequiredDocument } from "@fiduciaria/Shared/Types/required-document.interface";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { Observable, of } from "rxjs";
import { switchMap } from "rxjs/operators";

import { Unsubscriber } from "@shared/Components/Unsubscriber/unsubscriber.component";
import { ModalTitles, ModalType } from "@shared/Constantes/modal.constantes";
import { File } from "@shared/Entities/General/file.entity";
import { ModalService } from "@shared/Services/modal.service";

import { DocumentFile } from "../../Types/document-file";

import { UploadMultipleDocumentsModalComponent } from "./upload-multiple-documents-modal/upload-multiple-documents-modal.component";

@Component({
  selector: "app-documents",
  templateUrl: "./documents.component.html",
})
export class DocumentsComponent extends Unsubscriber implements OnInit {
  public form: FormGroup;
  public requiredDocuments$: Observable<RequiredDocument[]>;
  public isLessThan25Mb: boolean = true;

  @Output() public submitForm = new EventEmitter();
  private AcceptedFormats: string[] = [".pdf", "image/*", ".xls", ".doc"];
  private currentAcceptedFormat: string;
  private filesList: DocumentFile[];
  @Input() private currentFilesUpload: DocumentFile[];
  @Output() public currentFilesListEvent: EventEmitter<DocumentFile[]> = new EventEmitter<DocumentFile[]>();
  @Output() public currentFilesUploadAllEvent: EventEmitter<DocumentFile[]> = new EventEmitter<DocumentFile[]>();
  private _firstFile?: DocumentFile;

  constructor(
    private requestCreationForm: RequestCreationFormService,
    private trustService: TrustService,
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private bsModalService: BsModalService,
    private bsModalRef: BsModalRef,
    private uploadMultipleFilesService: UploadMultipleFilesService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentAcceptedFormat = this.AcceptedFormats.join(",");
    this.filesList = [];
    this.currentFilesUpload = [];
    this._firstFile = null;

    this.form = this.formBuilder.group({
      documents: this.formBuilder.array([]),
    });

    this.anotherSubscription = this.requestCreationForm.value
      .pipe(
        switchMap((value) => {
          if (!value?.escrow || !value?.type || !value?.subtype) return of([]);

          return this.trustService.getRequiredDocuments({
            dependecyId: value.area.key,
            typeId: value.type.key,
            subTypeId: value.subtype.key,
          });
        })
      )
      .subscribe((requiredDocuments) => {
        this.documentsControl.clear();
        requiredDocuments.forEach((document) => {
          const control = this.formBuilder.group({
            name: [document.sNombre],
            file: [],
          });

          if (document.bRequerido === "True") {
            control.get("file").setValidators(Validators.required);
          }
          this.documentsControl.push(control);
        });
      });

    this.anotherSubscription = this.requestCreationForm.reset.subscribe(() => {
      this.form.reset();
    });
  }

  public get invalidRequestFiles(): boolean {
    return this.uploadMultipleFilesService.invalidRequestFiles;
  }

  public checkFilesSize(document: DocumentFile[]): void {
    let totalBytesSum = 0;
    document.forEach((element) => {
      if (element?.file?.object?.size) {
        totalBytesSum += element?.file?.object?.size;
      }
    });
    const totalMbSum = this.bytesToMegaBytes(totalBytesSum);
    if (totalMbSum > 25) {
      this.isLessThan25Mb = false;
      this.modalService.showModalGeneral({
        type: ModalType.warning,
        title: ModalTitles.warning,
        body: "El tamaño máximo permitido para el total de archivos son 25 MB. Por favor, reduce el tamaño de los archivos o elije otros.",
      });
    } else {
      this.isLessThan25Mb = true;
    }
  }

  private bytesToMegaBytes(bytes: number): number {
    return this.uploadMultipleFilesService.bytesToMegaBytes(bytes);
  }

  private setFilesList(documents: FormArray): void {
    const values: DocumentFile[] = Array.from<DocumentFile>(documents?.value ?? []);
    this.filesList = [];

    values.forEach((value) => {
      if (value?.file) this.filesList.push(value);
    });

    this.uploadMultipleFilesService.init({
      filesList: this.filesList,
      currentFilesUpload: this.currentFilesUpload,
    });

    this.setCurrentFilesListEvent(this.filesList);
    this.setcurrentFilesUploadAllEvent(this.currentFilesUpload);
  }

  private setcurrentFilesUploadAllEvent(files: DocumentFile[]): void {
    this.currentFilesUploadAllEvent.emit(files);
  }

  private setCurrentFilesListEvent(files: DocumentFile[]): void {
    this.currentFilesListEvent.emit(files);
  }

  public openUploadDocumentsModal(reference: string): void {
    const files = this.filesList.filter((x) => x.name === reference);
    this.bsModalRef = this.bsModalService.show(UploadMultipleDocumentsModalComponent, {
      ignoreBackdropClick: true,
      class: "modal-lg",
      initialState: {
        reference: reference,
        files,
        currentFilesUpload: this.currentFilesUpload.filter((x) => x.name === reference),
        requestFiles: this.filesList.concat(this.currentFilesUpload),
        accept: this.currentAcceptedFormat,
        fileNameToName: this.fileNameToName,
        fileNameToExtension: this.fileNameToExtension,
        getFileSizeMegaBytes: this.getFileSizeMegaBytes,
        bytesToMegaBytes: this.bytesToMegaBytes,
        getSizeMegaBytes: this.getSizeMegaBytes,
        firstFile: files.length > 0 ? files[0] : null,
      } as Partial<UploadMultipleDocumentsModalComponent>,
    });

    this.bsModalRef.content?.onConfirm.subscribe((response) => {
      if (response) {
        let [modalType, modalMsg] = ["success", "Archivos cargados exitosamente!"];

        const everythingOk: boolean = !!this.bsModalRef?.content?.newFilesUpload;

        if (!everythingOk) [modalType, modalMsg] = ["warning", "Error al cargar los archivos"];

        const _firstFile: DocumentFile = this.bsModalRef?.content?.firstFile ?? null;

        if (_firstFile) {
          this._firstFile = _firstFile;
        }

        let _newCurrentFilesUploaded: DocumentFile[] = this.bsModalRef?.content?.newFilesUpload ?? [];
        if (_firstFile) _newCurrentFilesUploaded = _newCurrentFilesUploaded.filter((file) => file.file !== _firstFile.file);
        this.currentFilesUpload = this.addCurrentFilesUploaded(_newCurrentFilesUploaded, reference);

        this.setCurrentFilesListEvent(this.filesList);
        this.setcurrentFilesUploadAllEvent(this.currentFilesUpload);

        this.modalService.showModalGeneral({
          title: ModalTitles[modalType],
          body: modalMsg,
          type: ModalType[modalType],
        });
      }
    });
  }

  private addCurrentFilesUploaded(currentFilesUploaded: DocumentFile[], reference: string): DocumentFile[] {
    const _currentFilesUploadedByOtherReferences: DocumentFile[] = this.currentFilesUpload.filter((x) => x.name !== reference);
    return _currentFilesUploadedByOtherReferences.concat(currentFilesUploaded);
  }

  private fileNameToName(file: File): string {
    const name: string = file.name;
    return name.substring(0, name.lastIndexOf("."));
  }

  private fileNameToExtension(file: File): string {
    const extension: string = file.name;
    return extension.substring(extension.lastIndexOf(".") + 1, extension.length);
  }

  private getFileSizeMegaBytes(file: File, digits: number): number {
    return this.uploadMultipleFilesService.getFileSizeMegaBytes(file, digits);
  }

  private getSizeMegaBytes(bytes: number, digits: number): number {
    return this.uploadMultipleFilesService.getSizeMegaBytes(bytes, digits);
  }

  public get documentsControl(): FormArray {
    const documents = this.form.get("documents") as FormArray;
    this.setFilesList(documents);
    return documents;
  }

  public onSubmit(): void {
    this.requestCreationForm.addValue({ files: this.form.value.documents });
    this.submitForm.emit({ files: this.form.value.documents });
    this._firstFile = null;
  }
}
